import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import moment from 'moment-timezone';

import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { setConversations, setSelectedClient, resetState } from '@/store/textingPlatform/slice';
import { selectConversationsList, selectSelectedClient } from '@/store/textingPlatform/selector';

import MessagesList from './components/MessagesList';
import ConversationView from './components/ConversationView';
import BulkMessageModal from './components/BulkMessageModal';
import ContactModal from './components/ContactModal';
import BulkAIModal from './components/BulkAIModal';
import SetupPrompt from './components/SetupPrompt';
import LoadingComponent from '@/components/LoadingData/LoadingData';

import { TextingPlatformProps, ContactData } from '@/types/TTextingPlatform';
import {
	getContacts,
	getProvisionedNumber,
	getTextingConversations,
	getDisabledAutoReplySettings,
	updateContact,
	updateBulkAIAutoReply,
} from '@/api/textingPlatform';
import { BulkAIAutoReplyRequest } from '@/interfaces/ITextingPlatform';
import { useTextingPagination } from 'src/Hooks/useTexting';

const TextingPlatformArea = ({
	themeColor = '#007bff',
	premium_customer,
	theirNumber,
	username,
	selectedTimeZone,
	resellerObject,
}: TextingPlatformProps) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const selectedAgentId = useAppSelector(getSelectedAgentId) || '';
	const conversations = useSelector(selectConversationsList) || [];
	const selectedClient = useSelector(selectSelectedClient) || '';

	const [filterSearch, setFilterSearch] = useState('');
	const [filteredUnreplied, setFilteredUnreplied] = useState(false);
	const [textingGk, setTextingGk] = useState(false);
	const [contactAddingNumber, setContactAddingNumber] = useState('');
	const [createContactVal, setCreateContactVal] = useState('');
	const [isBulkModalActive, setIsBulkModalActive] = useState(false);
	const [isBulkAIModalActive, setIsBulkAIModalActive] = useState(false);
	const [isInSelectMode, setIsInSelectMode] = useState(false);
	const [selectedMsgs, setSelectedMsgs] = useState<string[]>([]);

	const { data: savedContacts = {} } = useQuery<ContactData>({
		queryKey: ['getContacts', username],
		queryFn: () => getContacts(username || ''),
		enabled: Boolean(username),
		retry: 2,
		staleTime: 300000,
	});

	const { data: provisionedNumber, isLoading: isLoadingProvisionedNumber } = useQuery<
		string | null
	>({
		queryKey: ['getProvisionedNumber', theirNumber],
		queryFn: () => getProvisionedNumber(theirNumber || ''),
		enabled: Boolean(theirNumber),
		retry: 2,
		staleTime: 300000,
	});

	const { loadMoreConversations, loadMoreMessages, hasMoreConversations, isLoadingMore } =
		useTextingPagination(selectedAgentId, provisionedNumber || '');

	const { isLoading: isLoadingConversations, refetch: refetchConversations } = useQuery({
		queryKey: ['getTextingConversations', selectedAgentId, provisionedNumber],
		queryFn: async () => {
			try {
				if (!selectedAgentId || !provisionedNumber) {
					throw new Error('Missing required parameters');
				}

				const result = await getTextingConversations(selectedAgentId, provisionedNumber, 'initial');
				if (result) {
					dispatch(setConversations(result));
				}

				return result;
			} catch (error) {
				dispatch(resetState());
				throw error;
			}
		},
		enabled: Boolean(selectedAgentId && provisionedNumber),
		retry: 2,
		staleTime: 30000,
	});

	const { data: dontAutoReplyToTheseNumbers = [], refetch: refetchAutoReplySettings } = useQuery<
		string[]
	>({
		queryKey: ['getDisabledAutoReplySettings', username],
		queryFn: () => getDisabledAutoReplySettings(username || ''),
		enabled: Boolean(username),
		retry: 2,
		staleTime: 300000,
	});

	const updateContactMutation = useMutation({
		mutationFn: ({
			contactAddingNumber,
			createContactVal,
			username,
		}: {
			contactAddingNumber: string;
			createContactVal: string;
			username: string;
		}) => updateContact(contactAddingNumber, createContactVal, username),
		onSuccess: (_, variables) => {
			notification.success({
				message: 'Success',
				description: 'Contact added successfully',
				duration: 3,
			});
			queryClient.invalidateQueries({ queryKey: ['getContacts', variables.username] });
			setContactAddingNumber('');
			setCreateContactVal('');
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error?.message || 'Something went wrong',
				duration: 5,
			});
		},
	});

	const updateBulkAIMutation = useMutation({
		mutationFn: (request: BulkAIAutoReplyRequest) => updateBulkAIAutoReply(request),
		onSuccess: (_, variables) => {
			notification.success({
				message: 'Success',
				description: 'Settings updated successfully',
				duration: 3,
			});
			queryClient.invalidateQueries({
				queryKey: ['getDisabledAutoReplySettings', variables.username],
			});
			setIsBulkAIModalActive(false);
			setSelectedMsgs([]);
			setIsInSelectMode(false);
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Error',
				description: error?.message || 'Failed to update AI Autoreply Settings',
				duration: 5,
			});
		},
	});

	useEffect(() => {
		try {
			if (resellerObject?.email_address === 'tom@prorfp.com') {
				setTextingGk(true);
			}
		} catch (_error) {
			notification.error({
				message: 'Error',
				description: 'Failed to set textingGk',
				duration: 3,
			});
		}
	}, [resellerObject]);

	const handleSetSelectedClient = (client: string) => {
		try {
			dispatch(setSelectedClient(client || ''));
		} catch (_error) {
			notification.error({
				message: 'Error',
				description: 'Failed to set selected client',
				duration: 3,
			});
		}
	};

	const updateContactName = () => {
		try {
			if (!contactAddingNumber || !username) {
				return;
			}
			updateContactMutation.mutate({ contactAddingNumber, createContactVal, username });
		} catch (_error) {
			notification.error({
				message: 'Error',
				description: 'Failed to update contact',
				duration: 3,
			});
		}
	};

	const setBulkAIAutoreply = (enable: boolean) => {
		try {
			if (!selectedMsgs.length || !username) {
				return;
			}
			updateBulkAIMutation.mutate({
				toNumbers: selectedMsgs,
				disableAiAutoReply: !enable,
				username,
			});
		} catch (_error) {
			notification.error({
				message: 'Error',
				description: 'Failed to update AI settings',
				duration: 3,
			});
		}
	};

	const renderPSTDate = (utcDate: string) => {
		try {
			if (!utcDate || !selectedTimeZone) {
				return '';
			}
			const pstDate = moment.utc(utcDate).tz(selectedTimeZone);
			return pstDate.format('M/D/YYYY, h:mm:ss A');
		} catch (_error) {
			return '';
		}
	};

	const renderPSTDateTruncated = (utcDate: string) => {
		try {
			if (!utcDate || !selectedTimeZone) {
				return '';
			}
			const pstDate = moment.utc(utcDate).tz(selectedTimeZone);
			const currentDate = moment().tz(selectedTimeZone);
			if (pstDate.isSame(currentDate, 'day')) {
				return pstDate.format('h:mm:ss A');
			} else {
				return pstDate.format('M/D/YYYY');
			}
		} catch (_error) {
			return '';
		}
	};

	const conversationsAsObject = conversations.reduce((acc, conv) => {
		if (conv?.client_number) {
			acc[conv.client_number] = conv;
		}
		return acc;
	}, {} as any);

	if (!premium_customer) {
		return (
			<SetupPrompt message="You must begin your free trial before accessing the texting platform." />
		);
	}

	if (isLoadingProvisionedNumber) {
		return (
			<div className="w-full bg-white rounded-lg shadow-lg">
				<div className="h-screen sm:h-[calc(100vh-175px)]">
					<LoadingComponent themeColor={themeColor} message="Loading Setup" />
				</div>
			</div>
		);
	}

	if (!provisionedNumber) {
		return <SetupPrompt message="You must setup your standalone number first" />;
	}

	if (isLoadingConversations) {
		return (
			<div className="w-full bg-white rounded-lg shadow-lg">
				<div className="h-screen sm:h-[calc(100vh-175px)]">
					<LoadingComponent themeColor={themeColor} message="Loading Conversations" />
				</div>
			</div>
		);
	}

	return (
		<div className="w-full bg-white rounded-lg shadow-lg">
			<div className="flex h-[calc(100vh-160px)] sm:h-[calc(100vh-100px)]">
				<div
					className={`${selectedClient ? 'hidden lg:block lg:w-80' : 'w-full lg:w-80'} flex-shrink-0`}
				>
					<MessagesList
						msgs={conversations}
						filterSearch={filterSearch}
						setFilterSearch={setFilterSearch}
						filteredUnreplied={filteredUnreplied}
						setFilteredUnreplied={setFilteredUnreplied}
						selectedClient={selectedClient}
						setSelectedClient={handleSetSelectedClient}
						savedContacts={savedContacts}
						provisionedNumber={provisionedNumber}
						dontAutoReplyToTheseNumbers={dontAutoReplyToTheseNumbers}
						renderPSTDateTruncated={renderPSTDateTruncated}
						setContactAddingNumber={setContactAddingNumber}
						setCreateContactVal={setCreateContactVal}
						isInSelectMode={isInSelectMode}
						setIsInSelectMode={setIsInSelectMode}
						selectedMsgs={selectedMsgs}
						setSelectedMsgs={setSelectedMsgs}
						setIsBulkModalActive={setIsBulkModalActive}
						setIsBulkAIModalActive={setIsBulkAIModalActive}
						getTextingConvos={refetchConversations}
						textingGk={textingGk}
						themeColor={themeColor}
						hasMoreConversations={hasMoreConversations}
						isLoadingMore={isLoadingMore}
						loadMoreConversations={loadMoreConversations}
					/>
				</div>

				<div className={`${selectedClient ? 'w-full' : 'hidden lg:block lg:flex-1'} flex-1`}>
					<ConversationView
						selectedClient={selectedClient}
						messagesData={conversationsAsObject}
						savedContacts={savedContacts}
						dontAutoReplyToTheseNumbers={dontAutoReplyToTheseNumbers}
						renderPSTDate={renderPSTDate}
						username={username || ''}
						provisionedNumber={provisionedNumber}
						theirNumber={theirNumber || ''}
						getTextingConvos={refetchConversations}
						getDisabledAutoReplySettings={refetchAutoReplySettings}
						themeColor={themeColor}
						selectedAgentId={selectedAgentId}
						setSelectedClient={handleSetSelectedClient}
						loadMoreMessages={loadMoreMessages}
					/>
				</div>
			</div>

			<ContactModal
				visible={Boolean(contactAddingNumber)}
				onClose={() => setContactAddingNumber('')}
				contactValue={createContactVal}
				setContactValue={setCreateContactVal}
				onSave={updateContactName}
				themeColor={themeColor}
			/>

			<BulkMessageModal
				visible={isBulkModalActive}
				onClose={() => setIsBulkModalActive(false)}
				provisionedNumber={provisionedNumber}
				theirNumber={theirNumber || ''}
				username={username || ''}
				getTextingConvos={refetchConversations}
				getContacts={() => queryClient.invalidateQueries({ queryKey: ['getContacts', username] })}
				themeColor={themeColor}
				selectedAgentId={selectedAgentId}
			/>

			<BulkAIModal
				visible={isBulkAIModalActive}
				onClose={() => setIsBulkAIModalActive(false)}
				selectedMsgs={selectedMsgs}
				setBulkAIAutoreply={setBulkAIAutoreply}
				themeColor={themeColor}
			/>
		</div>
	);
};

export default TextingPlatformArea;
